import styled from '@emotion/styled'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  accordionSummaryClasses,
  Box,
  Typography,
  Button,
  Chip,
  IconButton
} from '@mui/material'
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import Close from '@mui/icons-material/Close'

export const StyledExpandIcon = styled(ExpandCircleDownOutlinedIcon)(({theme}) => ({
  fontSize: theme.typography.kpiMedium.fontSize,
  color: theme.palette.hpBase,
  transform: 'rotate(180deg)'
}))

export const StyledCollapseIcon = styled(ExpandCircleDownOutlinedIcon)(({theme}) => ({
  fontSize: theme.typography.kpiMedium.fontSize,
  color: theme.palette.hpBase
}))

export const AccordionWrapper = styled('div')(({theme}) => ({
  marginBottom: theme.spacing(1.5)
}))

export const StyledProjectAccordion = styled((props) => <Accordion disableGutters elevation={0} square {...props} />)(
  ({theme}) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '4px !important',
    backgroundColor: theme.palette.hpWhite,
    overflow: 'visible',
    '&:before': {
      display: 'none' // Removes the default divider line
    },
    '&.Mui-expanded': {
      marginTop: 0
    }
  })
)

export const StyledProjectAccordionSummary = styled(AccordionSummary)(({theme}) => ({
  flexDirection: 'row',
  padding: `${theme.spacing(0.5)} ${theme.spacing(2)}`,
  minHeight: 48,
  backgroundColor: theme.palette.hpWhite,
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: theme.palette.hpWhite
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    order: -1, // Move icon to the left
    marginRight: theme.spacing(1.5),
    transform: 'rotate(0deg)',
    transition: 'transform 150ms'
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: 'rotate(0deg)'
  },
  [`& .${accordionSummaryClasses.content}`]: {
    margin: `${theme.spacing(1)} 0`,
    alignItems: 'center'
  }
}))

export const StyledProjectAccordionDetails = styled(AccordionDetails)(({theme}) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  backgroundColor: theme.palette.hpWhite
}))

/**
 * Section title within accordion header
 */
export const AccordionTitle = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.h6.fontSize,
  fontWeight: theme.typography.fontWeightSemibold,
  color: theme.palette.hpBlack
}))

/**
 * Container for section group headers (GEOGRAPHIES, FILTERS, etc.)
 */
export const SectionGroupContainer = styled(Box)(({theme}) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1.5)
}))

/**
 * Section group label (GEOGRAPHIES, FILTERS, FEATURE PERMISSIONS)
 */
export const SectionGroupLabel = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.hpPrimary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
}))

/**
 * Container for "All Project Permissions" header with Expand/Collapse button
 */
export const PermissionsHeaderContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1)
}))

/**
 * "All Project Permissions" title
 */
export const PermissionsTitle = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.h6.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.hpAccentBlack
}))

/**
 * Expand All / Collapse All button
 */
export const ExpandCollapseButton = styled(Button)(({theme}) => ({
  textTransform: 'none',
  color: theme.palette.hpPrimary,
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline'
  }
}))

/**
 * Container for selected filters/items with grey background
 * This displays the chips for selected items
 */
export const SelectedItemsContainer = styled(Box)(({theme}) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius || 4,
  marginTop: theme.spacing(1),
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  minHeight: '40px'
}))

/**
 * Label for selected items count
 */
export const SelectedItemsLabel = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.subtitle2.fontSize,
  fontStyle: 'normal',
  color: theme.palette.hpBase,
  marginRight: theme.spacing(1),
  display: 'flex',
  alignItems: 'center'
}))

/**
 * Styled icon components for expand/collapse all button
 */
export const StyledExpandAllIcon = styled(UnfoldMoreIcon)(({theme}) => ({
  fontSize: theme.typography.h6.fontSize,
  marginRight: theme.spacing(0.5)
}))

export const StyledCollapseAllIcon = styled(UnfoldLessIcon)(({theme}) => ({
  fontSize: theme.typography.h6.fontSize,
  marginRight: theme.spacing(0.5)
}))

/**
 * Styled components for SelectedFiltersPreview
 */
export const FilterPreviewHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1)
}))

export const StyledFilterChip = styled(Chip)(({theme}) => ({
  '& .MuiChip-label': {
    fontSize: theme.typography.caption.fontSize
  },
  '&:hover': {
    backgroundColor: theme.palette.grey[50]
  }
}))

export const RemoveIconButton = styled(IconButton)(({theme}) => ({
  padding: theme.spacing(0.25)
}))

export const RemoveIcon = styled(Close)(({theme}) => ({
  fontSize: 16
}))
